import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { RootState } from 'store';

// import AddIcon from '@mui/icons-material/Add';
// import CheckIcon from '@mui/icons-material/Check';
// import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
// import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import SnackBar, { MessageType } from 'components/SnackBar';
import { projectModalActions, TASK_MODAL } from 'features/Project/projectModalSlice';
import { projectFilterActions } from 'features/Project/projectSlice';
import { useUpdateJobTaskByIdMutation } from 'services/busmanApi';
import { TaskRequest } from 'types/JobTypes';
import { ProjectManagementJobResponse } from 'types/ProjectTypes';
import { TASK_STATUS } from 'utils/constants';

interface ProjectJobBoardProps {
  isLoading: boolean;
  projectJobs: ProjectManagementJobResponse[] | [];
}

// Statuses that are backed by the API. Custom columns added on the board use a
// generated id as their status and are not persisted.
const PERSISTABLE_STATUSES = new Set(Object.values(TASK_STATUS).map((status) => status.value));

const MAX_COLUMNS = 8;

// A task enriched with its parent job context so the card can show where it belongs.
type BoardTask = ProjectManagementJobResponse['task_list'][number] & {
  jobId: string;
  jobName: string;
  jobNumber: string;
  introducedInScopeId: string;
};

type ColumnDef = { id: string; title: string; status: string; aliases?: string[]; color?: string | null };

interface ColumnHeaderProps {
  title: string;
  color?: string | null;
  disableAdding: boolean;
  disableDeleting: boolean;
  onAddColumn: () => void;
  onRenameColumn: (title: string) => void;
  onSetColor: (color: string | null) => void;
  onDeleteColumn: () => void;
}

interface DroppableColumnProps {
  col: ColumnDef;
  tasks: BoardTask[];
  disableAdding: boolean;
  disableDeleting: boolean;
  onAddColumn: () => void;
  onRenameColumn: (title: string) => void;
  onSetColor: (color: string | null) => void;
  onDeleteColumn: () => void;
  onEditTask: (task: BoardTask) => void;
}

// Map each task status to an optional column accent colour.
const STATUS_COLORS: Record<string, string> = {
  [TASK_STATUS.INPROGRESS.value]: '#f5a623',
  [TASK_STATUS.COMPLETED.value]: '#4caf50',
  [TASK_STATUS.CANCELLED.value]: '#e24b4a',
  [TASK_STATUS.ONHOLD.value]: '#2196f3',
};

// Columns are seeded from the shared TASK_STATUS values so the board stays in
// sync with the statuses used everywhere else (task drawer, line items, etc.).
const INITIAL_COLUMNS: ColumnDef[] = Object.values(TASK_STATUS).map((status) => ({
  id: status.value,
  title: status.name.toUpperCase(),
  status: status.value,
  color: STATUS_COLORS[status.value] ?? null,
}));

const COLUMN_COLOR_OPTIONS: { label: string; value: string | null }[] = [
  { label: 'None', value: null },
  { label: 'Amber', value: '#f5a623' },
  { label: 'Green', value: '#4caf50' },
  { label: 'Red', value: '#e24b4a' },
  { label: 'Blue', value: '#2196f3' },
  { label: 'Purple', value: '#9531FF' },
];

// Flatten every job's task_list into a single list of board cards, tagging each
// task with its parent job's name/number for display.
const flattenTasks = (jobs: ProjectManagementJobResponse[]): BoardTask[] =>
  jobs.flatMap((job) =>
    job.task_list.map((task) => ({
      ...task,
      jobId: job.job_id,
      jobName: job.job_name,
      jobNumber: job.job_number,
      introducedInScopeId: job.introduced_in_scope_id,
    })),
  );

const TaskCard = ({
  task,
  isDragging = false,
  onEdit,
}: {
  task: BoardTask;
  isDragging?: boolean;
  onEdit?: () => void;
}) => {
  const theme = useTheme();
  const { assigneeList } = useSelector((state: RootState) => state.projectFilterReducer);

  const assigneeName =
    task.assigned_to_id === null
      ? 'Not assigned'
      : assigneeList.find((item) => item.user_id === task.assigned_to_id)?.full_name ?? 'Not assigned';

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        boxShadow: isDragging ? theme.shadows[6] : theme.shadows[1],
        p: 1.5,
        cursor: 'grab',
        ':hover': { boxShadow: theme.shadows[3] },
      }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {task.name}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
            {task.jobName} · {task.jobNumber || '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {task.percent_complete ?? 0}%
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <PersonOutlineIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} noWrap>
          {assigneeName}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Stack direction="column">
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Priority: {task.priority || '—'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Due Date: {task.due_date || '—'}
            </Typography>
          </Stack>
        </Box>
        {onEdit && (
          <Tooltip title="Edit task">
            <IconButton
              size="small"
              aria-label={`edit-task-${task.name}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}>
              <EditIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

const DraggableCard = ({ task, onEdit }: { task: BoardTask; onEdit: () => void }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.4 : 1,
        outline: 'none',
      }}>
      <TaskCard task={task} onEdit={onEdit} />
    </Box>
  );
};

const ColumnHeader = ({
  title,
  color,
  // disableAdding,
  // disableDeleting,
  // onAddColumn,
  onRenameColumn,
  onSetColor,
}: // onDeleteColumn,
ColumnHeaderProps) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const commit = () => {
    const next = draftTitle.trim() || title;
    onRenameColumn(next);
    setDraftTitle(next);
    setIsEditing(false);
  };

  const cancel = () => {
    setDraftTitle(title);
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        pb: 1.5,
      }}>
      {isEditing ? (
        <TextField
          size="small"
          inputRef={inputRef}
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          variant="standard"
          inputProps={{ 'aria-label': `rename-${title}`, style: { letterSpacing: 1, fontSize: '0.875rem' } }}
        />
      ) : (
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled, letterSpacing: 1 }}>
          {title}
        </Typography>
      )}
      <Box>
        {/* <IconButton size="small" aria-label={`add-column-from-${title}`} onClick={onAddColumn} disabled={disableAdding}>
          <AddIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        </IconButton>
        <IconButton size="small" aria-label={`color-${title}`} onClick={(e) => setColorAnchor(e.currentTarget)}>
          <PaletteOutlinedIcon fontSize="small" sx={{ color: color || theme.palette.text.disabled }} />
        </IconButton>
        <Tooltip title={disableDeleting ? 'At least four board is required' : 'Delete board'}>
          <span>
            <IconButton size="small" aria-label={`delete-${title}`} onClick={onDeleteColumn} disabled={disableDeleting}>
              <DeleteOutlineIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          aria-label={isEditing ? `confirm-rename-${title}` : `edit-${title}`}
          onClick={() => (isEditing ? commit() : setIsEditing(true))}>
          {isEditing ? (
            <CheckIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
          ) : (
            <EditIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
          )}
        </IconButton> */}
        <Menu anchorEl={colorAnchor} open={Boolean(colorAnchor)} onClose={() => setColorAnchor(null)}>
          {COLUMN_COLOR_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              onClick={() => {
                onSetColor(option.value);
                setColorAnchor(null);
              }}
              selected={(color ?? null) === option.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,

                    height: 16,
                    borderRadius: '50%',
                    background: option.value || 'transparent',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <Typography variant="body2">{option.label}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

const DroppableColumn = ({
  col,
  tasks,
  disableAdding,
  disableDeleting,
  onAddColumn,
  onRenameColumn,
  onSetColor,
  onDeleteColumn,
  onEditTask,
}: DroppableColumnProps) => {
  const theme = useTheme();
  const { setNodeRef, isOver } = useDroppable({ id: col.id });

  const tintedBackground = col.color ? `${col.color}1A` : theme.palette.background.default;

  return (
    <Box
      ref={setNodeRef}
      sx={{
        p: 2,
        flex: 1,
        minWidth: 0,
        display: 'flex',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRadius: '6px',
        scrollbarWidth: 'thin',
        flexDirection: 'column',
        border: col.color ? `2px solid ${col.color}` : '2px solid transparent',
        backgroundColor: isOver ? theme.palette.action.hover : tintedBackground,
        transition: 'background-color 0.15s, border-color 0.15s',
      }}>
      <ColumnHeader
        title={col.title}
        color={col.color}
        onAddColumn={onAddColumn}
        onRenameColumn={onRenameColumn}
        onSetColor={onSetColor}
        onDeleteColumn={onDeleteColumn}
        disableAdding={disableAdding}
        disableDeleting={disableDeleting}
      />
      <Stack spacing={2}>
        {tasks.map((task) => (
          <DraggableCard key={task.id} task={task} onEdit={() => onEditTask(task)} />
        ))}
      </Stack>
    </Box>
  );
};

const matchesColumn = (col: ColumnDef, status: string) => col.status === status || col.aliases?.includes(status);

/**
 *  @returns: Project Job Board
 */
const ProjectJobBoard = ({ isLoading, projectJobs }: ProjectJobBoardProps) => {
  const dispatch = useDispatch();
  const { assignee } = useSelector((state: RootState) => state.projectFilterReducer);
  const [updateJobTask] = useUpdateJobTaskByIdMutation();

  const [tasks, setTasks] = useState<BoardTask[]>([]);
  const [columns, setColumns] = useState<ColumnDef[]>(INITIAL_COLUMNS);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [message, setMessage] = useState<MessageType>({ type: '', msg: '' });

  // Seed the board from the fetched jobs whenever the query data changes.
  useEffect(() => {
    setTasks(projectJobs ? flattenTasks(projectJobs) : []);
  }, [projectJobs]);

  const disableAdding = columns.length >= MAX_COLUMNS;
  const disableDeleting = columns.length <= 4;
  const pendingDeleteCol = columns.find((c) => c.id === pendingDeleteId) || null;
  const pendingDeleteTaskCount = pendingDeleteCol
    ? tasks.filter((t) => matchesColumn(pendingDeleteCol, t.status)).length
    : 0;

  // Show only the selected assignee's tasks on the board. 'All' keeps every task.
  // The underlying `tasks` state is left intact so drag/persist still works.
  const visibleTasks = React.useMemo(
    () => (assignee === 'All' ? tasks : tasks.filter((t) => t.assigned_to_id === assignee)),
    [tasks, assignee],
  );

  // If the selected assignee no longer has any tasks on the board, fall back to 'All'.
  useEffect(() => {
    if (assignee !== 'All' && tasks.length > 0 && !tasks.some((t) => t.assigned_to_id === assignee)) {
      dispatch(projectFilterActions.setAssignee('All'));
    }
  }, [assignee, tasks, dispatch]);

  const activeTask = tasks.find((t) => t.id === activeId) || null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const targetCol = columns.find((c) => c.id === over.id);
    if (!targetCol) return;

    const movedTask = tasks.find((t) => t.id === active.id);
    if (!movedTask || movedTask.status === targetCol.status) return;

    const previousStatus = movedTask.status;

    // Optimistically reflect the move on the board.
    setTasks((prev) => prev.map((t) => (t.id === active.id ? { ...t, status: targetCol.status } : t)));

    // Custom columns aren't backed by a real task status, so there's nothing to persist.
    if (!PERSISTABLE_STATUSES.has(targetCol.status)) return;

    // Strip the board-only fields before sending the task back to the API.
    const { jobId, jobName, jobNumber, introducedInScopeId, id, ...taskFields } = movedTask;
    void jobId;
    void jobName;
    void jobNumber;
    void introducedInScopeId;
    const response = await updateJobTask({
      taskId: id,
      updateTaskPayload: { ...taskFields, status: targetCol.status },
    });

    if ('error' in response) {
      // Roll the card back to its original column on failure.
      setTasks((prev) => prev.map((t) => (t.id === active.id ? { ...t, status: previousStatus } : t)));
      setMessage({ type: 'error', msg: 'Error updating task status' });
      return;
    }

    setMessage({ type: 'success', msg: 'Task status updated.' });
  };

  // Open the shared task drawer (rendered by ProjectManagementJob) in edit mode.
  const handleEditTask = (task: BoardTask) => {
    const { jobId, jobName, jobNumber, introducedInScopeId, ...taskFields } = task;
    void jobName;
    void jobNumber;

    const editTask: TaskRequest = {
      ...taskFields,
      id: task.id,
      job_id: jobId,
      introduced_in_scope_id: introducedInScopeId,
      removed_in_scope: null,
      assigned_to_id: task.assigned_to_id ?? '',
      estimated_hours: Number(task.estimated_hours) ?? 0,
      actual_hours: Number(task.actual_hours) ?? 0,
    };

    dispatch(projectModalActions.openEditModal({ type: TASK_MODAL, data: editTask }));
  };

  const addColumnAfter = (afterId: string) => {
    setColumns((prev) => {
      if (prev.length >= MAX_COLUMNS) return prev;
      const newId = `col-${Date.now()}`;
      const newTitle = 'NEW COLUMN';
      const newCol: ColumnDef = { id: newId, title: newTitle, status: newId };
      const insertAt = prev.findIndex((c) => c.id === afterId);
      if (insertAt === -1) return [...prev, newCol];
      const next = [...prev];
      next.splice(insertAt + 1, 0, newCol);
      return next;
    });
  };

  const renameColumn = (id: string, title: string) => {
    setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
  };

  const setColumnColor = (id: string, color: string | null) => {
    setColumns((prev) => prev.map((c) => (c.id === id ? { ...c, color } : c)));
  };

  const confirmDeleteColumn = () => {
    if (!pendingDeleteCol) return;
    const remaining = columns.filter((c) => c.id !== pendingDeleteCol.id);
    const fallback = remaining[0];

    if (fallback) {
      setTasks((prev) =>
        prev.map((t) => (matchesColumn(pendingDeleteCol, t.status) ? { ...t, status: fallback.status } : t)),
      );
    }
    setColumns(remaining);
    setPendingDeleteId(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, height: '95vh', flexDirection: 'row' }}>
        {columns.map((col) => (
          <Box key={col.id} sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" animation="wave" width="100%" height="96px" />
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
      <Box
        sx={{
          gap: 1,
          display: 'flex',
          height: '95vh',
          overflow: 'hidden',
          flexDirection: 'row',
        }}>
        {columns.map((col) => {
          const columnTasks = visibleTasks.filter((t) => matchesColumn(col, t.status));
          return (
            <DroppableColumn
              key={col.id}
              col={col}
              tasks={columnTasks}
              disableAdding={disableAdding}
              disableDeleting={disableDeleting}
              onAddColumn={() => addColumnAfter(col.id)}
              onRenameColumn={(title) => renameColumn(col.id, title)}
              onSetColor={(color) => setColumnColor(col.id, color)}
              onDeleteColumn={() => setPendingDeleteId(col.id)}
              onEditTask={handleEditTask}
            />
          );
        })}
      </Box>
      <DragOverlay>{activeTask ? <TaskCard task={activeTask} isDragging /> : null}</DragOverlay>
      <Dialog open={Boolean(pendingDeleteCol)} onClose={() => setPendingDeleteId(null)}>
        <DialogTitle>Delete board?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteCol
              ? pendingDeleteTaskCount > 0
                ? `"${pendingDeleteCol.title}" has ${pendingDeleteTaskCount} task${
                    pendingDeleteTaskCount === 1 ? '' : 's'
                  }. They will be moved to "${columns.find((c) => c.id !== pendingDeleteCol.id)?.title ?? ''}".`
                : `Delete "${pendingDeleteCol.title}"? This cannot be undone.`
              : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteId(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDeleteColumn}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBar
        message={message}
        onClose={() => setMessage({ type: '', msg: '' })}
        snackbarOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </DndContext>
  );
};

export default ProjectJobBoard;
